/**
 * Phase 256 — Bulk Onboard Modal
 *
 * Three-step CSV-driven workflow for bulk-creating students or employees:
 *   1. Drop / paste CSV (or download the template)
 *   2. Preview + inline-edit the parsed grid + remove bad rows
 *   3. Submit → results panel (created / skipped / errored)
 *
 * Reuses the existing `POST /api/onboarding/bulk` endpoint that fans
 * each row into the standard `create_student` / `create_employee` flow,
 * so every Phase 245 financial profile field, custom_id sequencing,
 * Phase 96 FK mirrors, and Phase 95-style branded welcome email all
 * fire automatically. WhatsApp credential dispatch is layered on top
 * server-side when a phone number is present.
 */
import { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, Trash2, Loader2, CheckCircle2, XCircle,
  AlertTriangle, Download, ArrowLeft, ArrowRight, Send, Mail, MessageCircle,
} from 'lucide-react';
import api from '@/lib/api';

const STUDENT_COLUMNS = [
  { key: 'name',           label: 'Name',           required: true,  width: 'w-40' },
  { key: 'email',          label: 'Email',          required: false, width: 'w-52' },
  { key: 'phone',          label: 'Phone',          required: false, width: 'w-36' },
  { key: 'dob',            label: 'DOB (YYYY-MM-DD)', required: false, width: 'w-32' },
  { key: 'sport',          label: 'Sport',          required: false, width: 'w-28' },
  { key: 'batch',          label: 'Batch',          required: false, width: 'w-28' },
  { key: 'guardian_name',  label: 'Guardian',       required: false, width: 'w-40' },
  { key: 'guardian_phone', label: 'Guardian Phone', required: false, width: 'w-36' },
];

const EMPLOYEE_COLUMNS = [
  { key: 'name',        label: 'Name',        required: true,  width: 'w-40' },
  { key: 'email',       label: 'Email',       required: false, width: 'w-52' },
  { key: 'phone',       label: 'Phone',       required: false, width: 'w-36' },
  { key: 'role',        label: 'Portal Role', required: false, width: 'w-28' },
  { key: 'department',  label: 'Department',  required: false, width: 'w-32' },
  { key: 'designation', label: 'Designation', required: false, width: 'w-36' },
  { key: 'base_salary', label: 'Base Salary', required: false, width: 'w-28' },
  { key: 'join_date',   label: 'Join Date',   required: false, width: 'w-32' },
];

const ROW_LIMIT = 500;

// Minimal RFC-4180-ish CSV parser. Handles quoted cells with commas + escaped quotes.
function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = '';
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuote) {
      if (ch === '"' && next === '"') { field += '"'; i++; }
      else if (ch === '"') { inQuote = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuote = true; }
      else if (ch === ',') { cur.push(field); field = ''; }
      else if (ch === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
      else if (ch === '\r') { /* ignore */ }
      else { field += ch; }
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows.filter(r => r.length > 1 || (r.length === 1 && r[0].trim() !== ''));
}

function rowsToObjects(csvRows, columns) {
  if (csvRows.length === 0) return [];
  const headerRow = csvRows[0].map(h => (h || '').trim().toLowerCase().replace(/\s+/g, '_'));
  // Build header → column key map
  const colKeys = columns.map(c => c.key);
  const headerMap = headerRow.map(h => colKeys.includes(h) ? h : null);
  return csvRows.slice(1).map(r => {
    const obj = {};
    columns.forEach(c => { obj[c.key] = ''; });
    headerMap.forEach((key, i) => {
      if (key) obj[key] = (r[i] || '').trim();
    });
    return obj;
  });
}

function buildCsvTemplate(columns) {
  const header = columns.map(c => c.label).join(',');
  const sample = columns.map(() => '').join(',');
  return `${header}\n${sample}\n`;
}

export default function BulkOnboardModal({ open, onClose, role = 'student', onComplete }) {
  const columns = role === 'employee' ? EMPLOYEE_COLUMNS : STUDENT_COLUMNS;
  const [step, setStep] = useState(1); // 1=intake, 2=preview, 3=results
  const [rawCsv, setRawCsv] = useState('');
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  // Phase 256b — track in-flight resend attempts + final status per row
  const [resendStatus, setResendStatus] = useState({}); // { [user_id]: 'sending' | 'sent' | 'failed' }
  const fileRef = useRef(null);

  const reset = useCallback(() => {
    setStep(1); setRawCsv(''); setRows([]); setResult(null); setIsDragOver(false); setResendStatus({});
  }, []);

  const handleClose = () => { reset(); onClose?.(); };

  const handleParse = useCallback((text) => {
    const csvRows = parseCsv(text);
    if (csvRows.length < 2) {
      toast.error('CSV needs a header row + at least one data row');
      return;
    }
    const parsed = rowsToObjects(csvRows, columns);
    const cleaned = parsed.filter(r => Object.values(r).some(v => v && v.trim()));
    if (cleaned.length === 0) {
      toast.error('No data rows found after parsing');
      return;
    }
    if (cleaned.length > ROW_LIMIT) {
      toast.error(`Max ${ROW_LIMIT} rows per batch. Got ${cleaned.length}.`);
      return;
    }
    setRows(cleaned);
    setStep(2);
  }, [columns]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      setRawCsv(text);
      handleParse(text);
    };
    reader.readAsText(file);
  };

  const onDrop = (e) => {
    e.preventDefault(); setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const updateCell = (idx, key, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  };

  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const downloadTemplate = () => {
    const blob = new Blob([buildCsvTemplate(columns)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `outplay-${role}-bulk-template.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    const validRows = rows.filter(r => (r.name || '').trim());
    if (validRows.length === 0) {
      toast.error('No rows with a name'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        role,
        items: validRows.map(r => {
          const item = { name: r.name };
          columns.forEach(c => {
            if (c.key !== 'name' && r[c.key]) {
              item[c.key] = c.key === 'base_salary' ? Number(r[c.key]) || 0 : r[c.key];
            }
          });
          return item;
        }),
      };
      const res = await api.post('/onboarding/bulk', payload);
      setResult(res.data);
      setStep(3);
      onComplete?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk onboarding failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 256b — Resend credentials for a row whose initial dispatch failed.
  const handleResend = async (row, channel = 'email') => {
    if (!row.login_password) {
      toast.error('No temp password available — open the per-row credential modal instead');
      return;
    }
    setResendStatus(prev => ({ ...prev, [row.id]: 'sending' }));
    try {
      const res = await api.post('/onboarding/resend-credentials', {
        role,
        user_id: row.id,
        password: row.login_password,
        channel,
      });
      const sent = res.data?.sent_via || [];
      if (sent.length > 0) {
        toast.success(`Credentials re-sent via ${sent.join(' + ')} to ${row.name}`);
        setResendStatus(prev => ({ ...prev, [row.id]: 'sent' }));
      } else {
        toast.error(res.data?.failures?.[0]?.reason || 'Delivery failed');
        setResendStatus(prev => ({ ...prev, [row.id]: 'failed' }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
      setResendStatus(prev => ({ ...prev, [row.id]: 'failed' }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-onboard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Bulk Onboard {role === 'employee' ? 'Employees' : 'Students'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Step {step} of 3 &middot; Each row triggers the same single-create flow + branded welcome email + WhatsApp credentials.
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 — INTAKE */}
        {step === 1 && (
          <div className="space-y-4 overflow-y-auto" data-testid="bulk-onboard-step-1">
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
                isDragOver ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                           : 'border-slate-300 dark:border-slate-700 hover:border-emerald-400 hover:bg-slate-50 dark:hover:bg-slate-900/30'
              }`}
              data-testid="bulk-onboard-dropzone"
            >
              <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
              <p className="text-sm font-semibold">Drop a CSV here, or click to choose a file</p>
              <p className="text-xs text-muted-foreground mt-1">Max {ROW_LIMIT} rows per batch</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="bulk-onboard-file-input"
              />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Need a starter template?</span>
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="bulk-onboard-download-template">
                <Download className="h-3.5 w-3.5 mr-1" /> Download CSV template
              </Button>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                or paste CSV text
              </label>
              <Textarea
                placeholder={`${columns.map(c => c.label).join(',')}\nFirst Last,first@example.com,+9199...`}
                value={rawCsv}
                onChange={(e) => setRawCsv(e.target.value)}
                className="font-mono text-xs h-32"
                data-testid="bulk-onboard-paste-textarea"
              />
              <Button onClick={() => handleParse(rawCsv)} disabled={!rawCsv.trim()} className="w-full" data-testid="bulk-onboard-parse-btn">
                Parse CSV <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <div className="text-[11px] text-muted-foreground bg-slate-50 dark:bg-slate-900/40 border rounded-lg p-3">
              <strong>Expected columns:</strong> {columns.map(c => c.label).join(' &middot; ')}<br/>
              Only <strong>Name</strong> is required. Rows without an email skip the auto welcome email but still get a profile + custom_id.
            </div>
          </div>
        )}

        {/* STEP 2 — PREVIEW + EDIT */}
        {step === 2 && (
          <div className="space-y-3 overflow-hidden flex-1 flex flex-col" data-testid="bulk-onboard-step-2">
            <div className="flex items-center justify-between text-xs">
              <Badge variant="outline" data-testid="bulk-onboard-row-count">{rows.length} row(s) ready</Badge>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="bulk-onboard-back-to-intake">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Re-upload
              </Button>
            </div>

            <div className="overflow-auto border rounded-lg flex-1">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 dark:bg-slate-900/50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">#</th>
                    {columns.map(c => (
                      <th key={c.key} className={`${c.width} px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground`}>
                        {c.label}{c.required && <span className="text-red-500">*</span>}
                      </th>
                    ))}
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const nameOk = (row.name || '').trim().length > 0;
                    return (
                      <tr key={idx} className={`border-t ${!nameOk ? 'bg-red-50 dark:bg-red-950/20' : ''}`} data-testid={`bulk-onboard-row-${idx}`}>
                        <td className="px-2 py-1 text-muted-foreground">{idx + 1}</td>
                        {columns.map(c => (
                          <td key={c.key} className="px-1 py-1">
                            <Input
                              value={row[c.key] || ''}
                              onChange={(e) => updateCell(idx, c.key, e.target.value)}
                              className="h-7 text-xs"
                            />
                          </td>
                        ))}
                        <td className="px-2 py-1">
                          <Button variant="ghost" size="sm" onClick={() => removeRow(idx)} className="h-7 w-7 p-0" data-testid={`bulk-onboard-remove-row-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || rows.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-onboard-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                {submitting ? `Onboarding ${rows.length}...` : `Onboard ${rows.length} ${role}${rows.length === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3 — RESULTS */}
        {step === 3 && result && (
          <div className="space-y-4 overflow-y-auto" data-testid="bulk-onboard-step-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 mx-auto" />
                <div className="text-2xl font-black mt-1" data-testid="result-created-count">{result.created_count}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Created</div>
              </div>
              <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 text-center">
                <AlertTriangle className="h-6 w-6 text-amber-600 mx-auto" />
                <div className="text-2xl font-black mt-1" data-testid="result-skipped-count">{result.skipped_count}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Skipped</div>
              </div>
              <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4 text-center">
                <XCircle className="h-6 w-6 text-red-600 mx-auto" />
                <div className="text-2xl font-black mt-1" data-testid="result-error-count">{result.error_count}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Errors</div>
              </div>
            </div>

            {result.created?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Created accounts</h4>
                <div className="border rounded-lg max-h-48 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-emerald-50 dark:bg-emerald-950/30 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Name</th>
                        <th className="px-2 py-1.5 text-left">ID</th>
                        <th className="px-2 py-1.5 text-left">Email</th>
                        <th className="px-2 py-1.5 text-left">Notification</th>
                        <th className="px-2 py-1.5 text-left">Retry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.created.map(c => {
                        const rstat = resendStatus[c.id];
                        const initialFailed = c.notification_status === 'failed';
                        const finalSent = rstat === 'sent';
                        const showRetry = initialFailed && !finalSent && !!c.login_password;
                        return (
                          <tr key={c.id} className="border-t" data-testid={`result-row-${c.id}`}>
                            <td className="px-2 py-1">{c.name}</td>
                            <td className="px-2 py-1 font-mono text-[10px]">{c.custom_id || c.id?.slice(0, 8)}</td>
                            <td className="px-2 py-1">{c.email}</td>
                            <td className="px-2 py-1">
                              <Badge
                                variant={finalSent ? 'default' : (c.notification_status === 'sent' ? 'default' : 'outline')}
                                className={`text-[9px] ${finalSent ? 'bg-emerald-600' : ''}`}
                              >
                                {finalSent ? 'sent (resent)' : c.notification_status}
                              </Badge>
                            </td>
                            <td className="px-2 py-1">
                              {showRetry ? (
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResend(c, 'email')}
                                    disabled={rstat === 'sending' || !c.email}
                                    className="h-6 px-2 text-[10px]"
                                    data-testid={`resend-email-${c.id}`}
                                    title={!c.email ? 'No email on file' : 'Resend via email'}
                                  >
                                    {rstat === 'sending' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                                    <span className="ml-1">Email</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleResend(c, 'whatsapp')}
                                    disabled={rstat === 'sending' || !c.phone}
                                    className="h-6 px-2 text-[10px]"
                                    data-testid={`resend-whatsapp-${c.id}`}
                                    title={!c.phone ? 'No phone on file' : 'Resend via WhatsApp'}
                                  >
                                    <MessageCircle className="h-3 w-3" />
                                    <span className="ml-1">WA</span>
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-[10px] text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {result.skipped?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">Skipped (duplicates)</h4>
                <div className="border rounded-lg p-2 text-xs space-y-1 max-h-32 overflow-auto">
                  {result.skipped.map((s, i) => (
                    <div key={i}>Row {s.row}: <span className="font-medium">{s.name}</span> &mdash; <span className="text-muted-foreground">{s.reason}</span></div>
                  ))}
                </div>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-red-700 mb-2">Errors</h4>
                <div className="border border-red-200 rounded-lg p-2 text-xs space-y-1 max-h-32 overflow-auto bg-red-50/40 dark:bg-red-950/20">
                  {result.errors.map((e, i) => (
                    <div key={i}>Row {e.row}: <span className="font-medium">{e.name}</span> &mdash; <span className="text-red-700 dark:text-red-300">{e.error}</span></div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={reset} data-testid="bulk-onboard-import-more">Import more</Button>
              <Button onClick={handleClose} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-onboard-done">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
